import React, { useState, useEffect } from 'react';
import { 
    Box, Typography, Paper, Table, TableBody, TableCell, TableContainer, 
    TableHead, TableRow, CircularProgress, Alert, Button, Card, CardContent,
    Grid, TextField, InputAdornment, ToggleButtonGroup, ToggleButton, Avatar, Chip, Stack, Grow
} from '@mui/material';
import { 
    Search as SearchIcon, 
    ViewModule as ViewModuleIcon, 
    ViewList as ViewListIcon, 
    AccessTime as AccessTimeIcon,
    Refresh as RefreshIcon,
    Person as PersonIcon,
    Email as EmailIcon,
    LockOutlined as LockOutlinedIcon,
    OpenInNew as OpenInNewIcon,
    CheckCircle as CheckCircleIcon
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import config from '../config';

function Inbox() {
    const navigate = useNavigate();
    const [emails, setEmails] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [viewMode, setViewMode] = useState('grid');
    const [search, setSearch] = useState('');
    
    // Auth State
    const [authRequired, setAuthRequired] = useState(false);
    const [authData, setAuthData] = useState(null);
    const [polling, setPolling] = useState(false);

    useEffect(() => {
        fetchEmails();
    }, []);

    const fetchEmails = async () => {
        setLoading(true);
        setAuthRequired(false);
        try {
            const token = localStorage.getItem('token');
            const response = await axios.get(`${config}/api/inbox`, {
                headers: { Authorization: `Bearer ${token}` }
            });
            setEmails(response.data);
            setError(null);
        } catch (err) {
            console.error('Error fetching emails:', err);
            if (err.response && err.response.status === 403 && err.response.data.error === 'AuthRequired') {
                setAuthRequired(true);
                setError(null);
            } else {
                const errorMsg = err.response?.data?.error || 'Failed to load emails.';
                setError(errorMsg);
            }
        } finally {
            setLoading(false);
        }
    };

    const startLogin = async () => {
        setLoading(true);
        try {
            const token = localStorage.getItem('token');
            const response = await axios.get(`${config}/api/inbox/login`, {
                headers: { Authorization: `Bearer ${token}` }
            });
            setAuthData(response.data);
            pollForToken(response.data.flow);
        } catch (err) {
            setError('Failed to initiate login flow.');
        } finally {
            setLoading(false);
        }
    };

    const pollForToken = async (flow) => {
        setPolling(true);
        try {
            const token = localStorage.getItem('token');
            // This request will hang until user approves or it times out
            await axios.post(`${config}/api/inbox/poll`, { flow }, {
                headers: { Authorization: `Bearer ${token}` },
                timeout: 120000 // 2 minutes timeout
            });
            // If success
            setAuthData(null);
            setPolling(false);
            fetchEmails();
        } catch (err) {
            console.error("Polling error", err);
            setPolling(false);
            if (err.code === 'ECONNABORTED') {
                 setError('Login timed out. Please try again.');
            } else {
                 setError(err.response?.data?.error || 'Login failed.');
            }
        }
    };

    // Filter emails
    const filteredEmails = emails.filter(e => 
        e.subject.toLowerCase().includes(search.toLowerCase()) || 
        e.sender.toLowerCase().includes(search.toLowerCase()) ||
        (e.preview && e.preview.toLowerCase().includes(search.toLowerCase()))
    );

    if (authRequired) {
        return (
            <Box sx={{
                minHeight: '100vh',
                bgcolor: '#f4f6f8',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                px: 2
            }}>
                <Grow in={true} timeout={500}>
                    <Box sx={{ width: '100%', maxWidth: 520 }}>
                        {/* Header card */}
                        <Box sx={{
                            borderRadius: '20px 20px 0 0',
                            background: 'linear-gradient(135deg, #1e3c72 0%, #2a5298 100%)',
                            p: 4,
                            textAlign: 'center',
                            color: 'white',
                            boxShadow: '0 10px 30px rgba(30,60,114,0.25)',
                            position: 'relative',
                            overflow: 'hidden'
                        }}>
                            {/* decorative circles */}
                            <Box sx={{ position: 'absolute', top: -30, right: -30, width: 120, height: 120, borderRadius: '50%', bgcolor: 'rgba(255,255,255,0.06)' }} />
                            <Box sx={{ position: 'absolute', bottom: -20, left: -20, width: 80, height: 80, borderRadius: '50%', bgcolor: 'rgba(255,255,255,0.06)' }} />
                            <Box sx={{
                                width: 72, height: 72, borderRadius: '50%',
                                bgcolor: 'rgba(255,255,255,0.15)',
                                display: 'flex', alignItems: 'center', justifyContent: 'center',
                                mx: 'auto', mb: 2, position: 'relative', zIndex: 1
                            }}>
                                <LockOutlinedIcon sx={{ fontSize: 36, color: 'white' }} />
                            </Box>
                            <Typography variant="h5" fontWeight="800" sx={{ mb: 0.5, position: 'relative', zIndex: 1,color: 'white' }}>
                                Session Timed Out
                            </Typography>
                            <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.75)', position: 'relative', zIndex: 1 }}>
                                Your Office 365 session has expired. Reconnect to continue.
                            </Typography>
                        </Box>

                        {/* Body card */}
                        <Paper elevation={0} sx={{
                            borderRadius: '0 0 20px 20px',
                            p: 4,
                            bgcolor: 'white',
                            boxShadow: '0 20px 40px rgba(0,0,0,0.08)'
                        }}>
                            {error && (
                                <Alert severity="error" sx={{ mb: 3, borderRadius: 2 }}>{error}</Alert>
                            )}

                            {!authData ? (
                                <Box sx={{ textAlign: 'center' }}>
                                    <Typography variant="body2" color="#64748b" sx={{ mb: 3, lineHeight: 1.7 }}>
                                        Click <strong>Reconnect</strong> to start the Microsoft authentication flow.
                                        You will be given a code to enter on the Microsoft login page.
                                    </Typography>
                                    <Button
                                        variant="contained"
                                        fullWidth
                                        size="large"
                                        onClick={startLogin}
                                        disabled={loading}
                                        sx={{
                                            borderRadius: 3,
                                            fontWeight: '700',
                                            py: 1.5,
                                            fontSize: '1rem',
                                            background: 'linear-gradient(135deg, #1e3c72 0%, #2a5298 100%)',
                                            boxShadow: '0 6px 20px rgba(30,60,114,0.3)',
                                            textTransform: 'none',
                                            '&:hover': {
                                                background: 'linear-gradient(135deg, #162d56 0%, #1e3c72 100%)',
                                                transform: 'translateY(-1px)',
                                                boxShadow: '0 8px 24px rgba(30,60,114,0.4)'
                                            }
                                        }}
                                    >
                                        {loading
                                            ? <><CircularProgress size={20} sx={{ color: 'white', mr: 1 }} /> Connecting...</>
                                            : 'Reconnect to Office 365'
                                        }
                                    </Button>
                                </Box>
                            ) : (
                                <Box>
                                    {/* Step 1 */}
                                    <Box sx={{
                                        display: 'flex', alignItems: 'flex-start', gap: 2,
                                        p: 2.5, borderRadius: 3, bgcolor: '#f8fafc',
                                        border: '1px solid #e2e8f0', mb: 2
                                    }}>
                                        <Box sx={{
                                            minWidth: 32, height: 32, borderRadius: '50%',
                                            background: 'linear-gradient(135deg, #1e3c72 0%, #2a5298 100%)',
                                            display: 'flex', alignItems: 'center', justifyContent: 'center',
                                            color: 'white', fontWeight: '800', fontSize: '0.85rem'
                                        }}>1</Box>
                                        <Box sx={{ flex: 1 }}>
                                            <Typography variant="body2" fontWeight="700" color="#1e293b" sx={{ mb: 0.5 }}>
                                                Visit the Microsoft login page
                                            </Typography>
                                            <Button
                                                variant="outlined"
                                                size="small"
                                                endIcon={<OpenInNewIcon fontSize="small" />}
                                                href={authData.verification_uri}
                                                target="_blank"
                                                rel="noopener noreferrer"
                                                sx={{
                                                    borderRadius: 2, textTransform: 'none',
                                                    fontWeight: '600', borderColor: '#2a5298', color: '#2a5298',
                                                    '&:hover': { bgcolor: '#eff6ff', borderColor: '#1e3c72' }
                                                }}
                                            >
                                                {authData.verification_uri}
                                            </Button>
                                        </Box>
                                    </Box>

                                    {/* Step 2 – code + QR */}
                                    <Box sx={{
                                        p: 2.5, borderRadius: 3, bgcolor: '#f8fafc',
                                        border: '1px solid #e2e8f0', mb: 2,
                                        display: 'flex', gap: 2, alignItems: 'center'
                                    }}>
                                        <Box sx={{
                                            minWidth: 32, height: 32, borderRadius: '50%',
                                            background: 'linear-gradient(135deg, #1e3c72 0%, #2a5298 100%)',
                                            display: 'flex', alignItems: 'center', justifyContent: 'center',
                                            color: 'white', fontWeight: '800', fontSize: '0.85rem',
                                            alignSelf: 'flex-start', mt: 0.3
                                        }}>2</Box>
                                        <Box sx={{ flex: 1 }}>
                                            <Typography variant="body2" fontWeight="700" color="#1e293b" sx={{ mb: 1 }}>
                                                Enter this code
                                            </Typography>
                                            <Box sx={{
                                                display: 'inline-block',
                                                px: 3, py: 1.5, borderRadius: 2,
                                                background: 'linear-gradient(135deg, #1e3c72 0%, #2a5298 100%)',
                                                color: 'white', fontWeight: '900',
                                                fontSize: '1.5rem', letterSpacing: '0.25em',
                                                fontFamily: 'monospace',
                                                boxShadow: '0 4px 14px rgba(30,60,114,0.25)'
                                            }}>
                                                {authData.user_code}
                                            </Box>
                                        </Box>
                                        {authData.qr_code && (
                                            <Box sx={{
                                                border: '3px solid #e2e8f0', borderRadius: 3,
                                                p: 0.5, bgcolor: 'white', flexShrink: 0
                                            }}>
                                                <img
                                                    src={`data:image/png;base64,${authData.qr_code}`}
                                                    alt="Scan QR Code"
                                                    style={{ width: 90, height: 90, display: 'block' }}
                                                />
                                            </Box>
                                        )}
                                    </Box>

                                    {/* Waiting status */}
                                    {polling ? (
                                        <Box sx={{
                                            display: 'flex', alignItems: 'center', justifyContent: 'center',
                                            gap: 1.5, p: 2, borderRadius: 3,
                                            bgcolor: '#eff6ff', border: '1px solid #bfdbfe'
                                        }}>
                                            <CircularProgress size={18} sx={{ color: '#2a5298' }} />
                                            <Typography variant="body2" fontWeight="600" color="#1e3c72">
                                                Waiting for approval...
                                            </Typography>
                                        </Box>
                                    ) : (
                                        <Box sx={{
                                            display: 'flex', alignItems: 'center', justifyContent: 'center',
                                            gap: 1.5, p: 2, borderRadius: 3,
                                            bgcolor: '#f0fdf4', border: '1px solid #bbf7d0'
                                        }}>
                                            <CheckCircleIcon sx={{ color: '#16a34a', fontSize: 20 }} />
                                            <Typography variant="body2" fontWeight="600" color="#15803d">
                                                Approved! Loading inbox...
                                            </Typography>
                                        </Box>
                                    )}
                                </Box>
                            )}
                        </Paper>
                    </Box>
                </Grow>
            </Box>
        );
    }

    return (
        <Box sx={{ width: '100%', overflowX: 'hidden', boxSizing: 'border-box', pb: 6, px: { xs: 1, md: 3 }, bgcolor: '#f4f6f8', minHeight: '100vh' }}>
            <Grow in={true} timeout={500}>
                <Box sx={{ 
                    mb: 4, 
                    mt: 2, 
                    p: 4, 
                    borderRadius: 4, 
                    background: 'linear-gradient(135deg, #1e3c72 0%, #2a5298 100%)', 
                    color: 'white',
                    boxShadow: '0 10px 30px rgba(30, 60, 114, 0.2)',
                    position: 'relative',
                    overflow: 'hidden',
                    display: 'flex',
                    flexDirection: { xs: 'column', md: 'row' },
                    justifyContent: 'space-between',
                    alignItems: { xs: 'flex-start', md: 'center' },
                    gap: 3
                }}>
                    <Box sx={{ position: 'relative', zIndex: 2 }}>
                        <Typography variant="h4" fontWeight="800" sx={{ mb: 1, letterSpacing: 0.5, display: 'flex', alignItems: 'center', gap: 2, color: 'white' }}>
                            <EmailIcon sx={{ fontSize: 36, color: '#90caf9' }} />
                            Inbox
                        </Typography>
                        <Typography variant="body1" sx={{ color: 'rgba(255,255,255,0.8)' }}>
                            Sync and manage your Office 365 communications
                        </Typography>
                    </Box>

                    <Button 
                        variant="contained" 
                        startIcon={<RefreshIcon />} 
                        onClick={fetchEmails}
                        sx={{ 
                            position: 'relative',
                            zIndex: 2,
                            borderRadius: 2, 
                            fontWeight: 'bold', 
                            px: 3, 
                            py: 1,
                            bgcolor: 'white',
                            color: '#1e3c72',
                            boxShadow: '0 4px 14px rgba(0,0,0,0.1)',
                            '&:hover': { bgcolor: '#f1f5f9', transform: 'translateY(-2px)', boxShadow: '0 6px 20px rgba(0,0,0,0.15)' }
                        }}
                    >
                        Sync Now
                    </Button>
                </Box>
            </Grow>

            <Grow in={true} timeout={650}>
                <Card elevation={0} sx={{ mb: 4, p: 2, borderRadius: 4, border: '1px solid #f1f5f9', boxShadow: '0 4px 20px rgba(0,0,0,0.03)' }}>
                    <Box sx={{ display: 'flex', flexDirection: { xs: 'column', lg: 'row' }, justifyContent: 'space-between', alignItems: { xs: 'stretch', lg: 'center' }, gap: 2 }}>
                        
                        {/* Left: Filters (Not heavily used in Inbox, but maintaining similar layout style) */}
                        <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', alignItems: 'center' }}>
                            <Chip 
                                label="All Emails" 
                                sx={{ 
                                    fontWeight: '600', 
                                    borderRadius: 2,
                                    height: 32,
                                    bgcolor: '#1e293b',
                                    color: 'white',
                                    transition: 'all 0.2s ease'
                                }}
                            />
                            {/* Placeholder for future mail filters if needed */}
                        </Box>

                        {/* Right: Actions */}
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap', justifyContent: { xs: 'flex-start', lg: 'flex-end' } }}>
                            <TextField 
                                placeholder="Search emails..." 
                                size="small" 
                                value={search} 
                                onChange={e => setSearch(e.target.value)}
                                InputProps={{
                                    startAdornment: <InputAdornment position="start"><SearchIcon sx={{ color: '#94a3b8' }} /></InputAdornment>,
                                    sx: { 
                                        borderRadius: 3, 
                                        bgcolor: '#f8fafc',
                                        '& fieldset': { borderColor: '#e2e8f0' },
                                        '&:hover fieldset': { borderColor: '#cbd5e1' },
                                        '&.Mui-focused fieldset': { borderColor: '#3b82f6', borderWidth: 2 }
                                    }
                                }}
                                sx={{ width: { xs: '100%', sm: 300 } }}
                            />
                            
                            <ToggleButtonGroup
                                value={viewMode}
                                exclusive
                                onChange={(e, newMode) => { if (newMode) setViewMode(newMode); }}
                                size="small"
                                sx={{ 
                                    bgcolor: '#f8fafc', 
                                    borderRadius: 3,
                                    p: 0.5,
                                    gap: 0.5,
                                    '& .MuiToggleButton-root': { 
                                        border: 'none', 
                                        borderRadius: '8px !important',
                                        color: '#64748b',
                                        px: 1.5,
                                        py: 0.5
                                    },
                                    '& .Mui-selected': { bgcolor: 'white !important', color: '#3b82f6 !important', boxShadow: '0 2px 8px rgba(0,0,0,0.05)' }
                                }}
                            >
                                <ToggleButton value="grid" aria-label="grid view">
                                    <ViewModuleIcon fontSize="small" />
                                </ToggleButton>
                                <ToggleButton value="list" aria-label="list view">
                                    <ViewListIcon fontSize="small" />
                                </ToggleButton>
                            </ToggleButtonGroup>
                        </Box>
                    </Box>
                </Card>
            </Grow>

            {error && <Alert severity="error" sx={{ mb: 3, borderRadius: 3 }}>{error}</Alert>}

            {loading ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', p: 5 }}>
                    <CircularProgress sx={{ color: '#3b82f6' }} />
                </Box>
            ) : (
                viewMode === 'grid' ? (
                    <Grid container spacing={3}>
                        {filteredEmails.length === 0 ? (
                            <Grid item xs={12}>
                                <Paper elevation={0} sx={{ p: 6, textAlign: 'center', borderRadius: 4, border: '1px dashed #cbd5e1', bgcolor: 'transparent' }}>
                                    <ViewModuleIcon sx={{ fontSize: 48, color: '#94a3b8', mb: 2 }} />
                                    <Typography variant="h6" color="#475569" fontWeight="700">No emails found</Typography>
                                    <Typography color="#64748b" variant="body2">Try adjusting your search criteria.</Typography>
                                </Paper>
                            </Grid>
                        ) : filteredEmails.map((email) => (
                            <Grid item xs={12} md={6} lg={4} key={email.id}>
                                <Card 
                                    elevation={0}
                                    sx={{ 
                                        height: '100%', 
                                        borderRadius: 4, 
                                        border: '1px solid #f1f5f9',
                                        boxShadow: '0 4px 20px rgba(0,0,0,0.03)', 
                                        cursor: 'pointer', 
                                        transition: 'all 0.2s', 
                                        '&:hover': { transform: 'translateY(-2px)', boxShadow: '0 8px 30px rgba(0,0,0,0.08)', borderColor: '#e2e8f0' } 
                                    }} 
                                    onClick={() => navigate(`/inbox/${email.id}`)}
                                >
                                    <CardContent sx={{ p: 3, pt: 3.5, pb: '24px !important' }}>
                                        <Box sx={{ display: 'flex', alignItems: 'flex-start', mb: 2.5 }}>
                                            <Avatar sx={{ bgcolor: '#eff6ff', color: '#3b82f6', mr: 2.5, width: 44, height: 44 }}>
                                                <EmailIcon />
                                            </Avatar>
                                            <Box sx={{ overflow: 'hidden' }}>
                                                <Typography variant="h6" sx={{ fontWeight: '800', fontSize: '1rem', lineHeight: 1.3, mb: 0.5, color: '#1e293b' }}>
                                                    {email.subject}
                                                </Typography>
                                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                                    <PersonIcon sx={{ fontSize: 16, color: '#94a3b8' }} />
                                                    <Typography variant="caption" sx={{ color: '#64748b', fontWeight: 600 }} noWrap>
                                                        {email.sender}
                                                    </Typography>
                                                </Box>
                                            </Box>
                                        </Box>

                                        <Typography variant="body2" sx={{ 
                                            display: '-webkit-box',
                                            WebkitLineClamp: 3,
                                            WebkitBoxOrient: 'vertical',
                                            overflow: 'hidden',
                                            mb: 2.5,
                                            height: '4.5em',
                                            color: '#64748b',
                                            lineHeight: 1.5
                                        }}>
                                            {email.preview || "No preview available"}
                                        </Typography>

                                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', pt: 2, borderTop: '1px dashed #e2e8f0' }}>
                                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                <AccessTimeIcon sx={{ fontSize: 14, color: '#94a3b8' }} />
                                                <Typography variant="caption" sx={{ color: '#94a3b8', fontWeight: 500 }}>{email.date}</Typography>
                                            </Box>
                                        </Box>
                                    </CardContent>
                                </Card>
                            </Grid>
                        ))}
                    </Grid>
                ) : (
                    <Paper elevation={0} sx={{ borderRadius: 4, overflow: 'hidden', border: '1px solid #f1f5f9', boxShadow: '0 4px 20px rgba(0,0,0,0.03)', bgcolor: 'white' }}>
                        <TableContainer sx={{ width: '100%', display: 'block' }}>
                            <Table sx={{ minWidth: 650, tableLayout: 'auto', '& .MuiTableCell-root': { borderColor: '#f0f0f0', py: 2 } }}>
                                <TableHead sx={{ bgcolor: '#f8fafc' }}>
                                    <TableRow>
                                        <TableCell sx={{ fontWeight: '800', color: '#475569', textTransform: 'uppercase', fontSize: '0.75rem', letterSpacing: '0.5px' }}>Subject</TableCell>
                                        <TableCell sx={{ fontWeight: '800', color: '#475569', textTransform: 'uppercase', fontSize: '0.75rem', letterSpacing: '0.5px' }}>Sender</TableCell>
                                        <TableCell sx={{ fontWeight: '800', color: '#475569', textTransform: 'uppercase', fontSize: '0.75rem', letterSpacing: '0.5px' }}>Preview</TableCell>
                                        <TableCell sx={{ fontWeight: '800', color: '#475569', textTransform: 'uppercase', fontSize: '0.75rem', letterSpacing: '0.5px', align: 'right' }}>Date</TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {filteredEmails.map((email) => (
                                        <TableRow 
                                            key={email.id} 
                                            hover
                                            sx={{ '&:last-child td, &:last-child th': { border: 0 }, cursor: 'pointer', transition: 'background-color 0.2s', '&:hover': { bgcolor: '#f1f5f9 !important' } }}
                                            onClick={() => navigate(`/inbox/${email.id}`)}
                                        >
                                            <TableCell>
                                                <Typography variant="body2" sx={{ fontWeight: '700', color: '#1e293b' }}>
                                                    {email.subject}
                                                </Typography>
                                            </TableCell>
                                            <TableCell>
                                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                                                    <Avatar sx={{ width: 28, height: 28, fontSize: 13, fontWeight: 'bold', bgcolor: '#eff6ff', color: '#3b82f6' }}>
                                                        {email.sender.charAt(0).toUpperCase()}
                                                    </Avatar>
                                                    <Typography variant="body2" sx={{ color: '#475569', fontWeight: 600 }}>{email.sender}</Typography>
                                                </Box>
                                            </TableCell>
                                            <TableCell sx={{ maxWidth: 350 }}>
                                                <Typography variant="body2" sx={{ color: '#64748b' }} noWrap>
                                                    {email.preview || "No preview available"}
                                                </Typography>
                                            </TableCell>
                                            <TableCell align="right">
                                                <Typography variant="caption" sx={{ color: '#94a3b8', fontWeight: 600 }}>{email.date}</Typography>
                                            </TableCell>
                                        </TableRow>
                                    ))}
                                    {filteredEmails.length === 0 && (
                                        <TableRow>
                                            <TableCell colSpan={4} align="center" sx={{ py: 8 }}>
                                                <Typography variant="h6" color="#475569" fontWeight="700" gutterBottom>No emails found</Typography>
                                                <Typography color="#64748b" variant="body2">Try adjusting your search filters.</Typography>
                                            </TableCell>
                                        </TableRow>
                                    )}
                                </TableBody>
                            </Table>
                        </TableContainer>
                    </Paper>
                )
            )}
        </Box>
    );
}

export default Inbox;
